import { ManualTaskStatus } from '@compass/client/types/manual_task';
import { manualTaskService } from '@services/api';
import { queryKeys } from '@services/query.config';
import { useStore } from '@store/index';
import { useQuery } from '@tanstack/react-query';

import { CASES_QUERY_CONFIG } from '@/constants/queryConfig';
import { paginateCursorList } from '@/utils/paginateCursorList';

interface UseAllMyTasksParams {
  created_after?: string;
  created_before?: string;
}

const MAX_PAGES = 5;
const PAGE_SIZE = 100;

/**
 * Fetches the current user's manual tasks in the selected organization (all
 * programs, every status) for the given created_at window, up to
 * MAX_PAGES × PAGE_SIZE rows. My Tasks filters by tab and status client-side
 * over this one dataset, the same way My Cases does, so tab counts agree.
 */
export const useAllMyTasks = (params: UseAllMyTasksParams) => {
  const selectedOrganizationId = useStore(state => state.selectedOrganizationId);

  const query = useQuery({
    queryKey: queryKeys.myTasks.byOrg(selectedOrganizationId || '', {
      ...params,
      pageSize: PAGE_SIZE,
      maxPages: MAX_PAGES,
    }),
    queryFn: () =>
      paginateCursorList(
        async cursor => {
          const res = await manualTaskService.listMyManualTasks({
            organization_id: selectedOrganizationId!,
            created_after: params.created_after,
            created_before: params.created_before,
            limit: PAGE_SIZE,
            start_after_id: cursor,
          });
          return { records: res.manual_tasks, next_cursor: res.next_cursor };
        },
        MAX_PAGES,
        PAGE_SIZE,
      ),
    enabled: !!selectedOrganizationId,
    // Same reasoning as useAllMyCases: no 30s polling for a multi-page fan-out.
    ...CASES_QUERY_CONFIG,
    refetchInterval: false,
  });

  return { ...query, tasks: query.data?.records ?? [] };
};

const OPEN_STATUSES = [
  ManualTaskStatus.MANUAL_TASK_STATUS_PENDING,
  ManualTaskStatus.MANUAL_TASK_STATUS_STARTED,
];

/**
 * Number of the current user's open (pending or started) tasks across every program in the
 * selected organization, for the sidebar's My Tasks pill. Lives under myTasks.all, so task
 * status changes refresh it; the poll picks up tasks that workflows create.
 */
export const useMyOpenTaskCount = (enabled: boolean) => {
  const selectedOrganizationId = useStore(state => state.selectedOrganizationId);

  const { data } = useQuery({
    queryKey: queryKeys.myTasks.openCount(selectedOrganizationId || ''),
    queryFn: () =>
      manualTaskService.countMyManualTasks({
        organization_id: selectedOrganizationId!,
        statuses: OPEN_STATUSES,
      }),
    enabled: enabled && !!selectedOrganizationId,
    refetchInterval: 60 * 1000,
  });

  return data?.count ?? 0;
};
